import { create } from 'zustand'
import { Task, TaskType } from '../types'
import { genId, nowIso } from '../utils/format'

interface TaskStore {
  tasks: Task[]
  createTask: (type: TaskType, title: string, steps: string[]) => string
  simulate: (
    taskId: string,
    onComplete?: () => void,
    shouldFail?: boolean,
  ) => void
  cancelTask: (taskId: string) => void
}

const seed: Task[] = [
  {
    taskId: 'task_done_1',
    type: 'skill_generate',
    title: 'AI 创建 Skill：Nginx 日志分析',
    status: 'completed',
    progress: 100,
    steps: [
      { label: '理解需求提示词', done: true },
      { label: '规划 Skill 结构', done: true },
      { label: '生成代码与 manifest', done: true },
      { label: '安装到 Skills 中心', done: true },
    ],
    createdAt: '2026-06-03T09:00:00Z',
    updatedAt: '2026-06-03T09:40:00Z',
  },
  {
    taskId: 'task_failed_1',
    type: 'skill_url_install',
    title: '安装第三方 Skill：unknown-skill',
    status: 'failed',
    progress: 40,
    steps: [
      { label: '下载压缩包', done: true },
      { label: '安全校验 (大小/类型/哈希)', done: false },
      { label: '防路径穿越解压', done: false },
      { label: '注册到 Skills 中心', done: false },
    ],
    error: 'SHA256 校验失败，文件可能被篡改',
    createdAt: '2026-06-04T08:10:00Z',
    updatedAt: '2026-06-04T08:11:00Z',
  },
]

export const useTaskStore = create<TaskStore>((set, get) => ({
  tasks: seed,
  createTask: (type, title, steps) => {
    const taskId = genId('task')
    set((s) => ({
      tasks: [
        {
          taskId,
          type,
          title,
          status: 'running',
          progress: 0,
          steps: steps.map((label) => ({ label, done: false })),
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
        ...s.tasks,
      ],
    }))
    return taskId
  },
  simulate: (taskId, onComplete, shouldFail) => {
    const stepDuration = 900
    const task = get().tasks.find((t) => t.taskId === taskId)
    if (!task) return
    const total = task.steps.length

    const advance = (idx: number) => {
      const current = get().tasks.find((t) => t.taskId === taskId)
      if (!current || current.status === 'cancelled') return
      if (idx >= total) {
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.taskId === taskId
              ? {
                  ...t,
                  status: 'completed',
                  progress: 100,
                  updatedAt: nowIso(),
                }
              : t,
          ),
        }))
        onComplete?.()
        return
      }
      set((s) => ({
        tasks: s.tasks.map((t) =>
          t.taskId === taskId
            ? {
                ...t,
                steps: t.steps.map((st, i) =>
                  i === idx ? { ...st, done: true } : st,
                ),
                progress: Math.round(((idx + 1) / total) * 100),
                updatedAt: nowIso(),
              }
            : t,
        ),
      }))
      setTimeout(() => advance(idx + 1), stepDuration)
    }
    setTimeout(() => advance(0), stepDuration)
  },
  cancelTask: (taskId) =>
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.taskId === taskId &&
        (t.status === 'running' || t.status === 'pending')
          ? { ...t, status: 'cancelled', updatedAt: nowIso() }
          : t,
      ),
    })),
}))
